import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronLeft, Clock, Leaf, AlertTriangle, Sparkles } from 'lucide-react';
import FavoriteHeart from '@/components/FavoriteHeart';
import ReviewsSection from '@/components/ReviewsSection';

export default function HackDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [hack, setHack] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/hacks/${slug}`);
        if (!cancelled) setHack(data);
      } catch {
        if (!cancelled) setHack(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Skeleton className="h-72 rounded-2xl" />
        <div className="mt-8 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 space-y-4">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
          <Skeleton className="h-64 lg:col-span-5" />
        </div>
      </div>
    );
  }

  if (!hack) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <p className="font-display text-3xl">Hack not found.</p>
        <Button asChild className="mt-6 rounded-xl">
          <Link to="/diy">Back to DIY Lab</Link>
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="hack-detail-page" className="bg-background">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground gap-1 mb-6"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>

        <div className="relative rounded-2xl overflow-hidden border border-border/70 bg-card">
          <img src={hack.image_url} alt={hack.name} className="w-full h-72 sm:h-96 object-cover" onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=1200&q=80'; }} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />
          <div className="absolute top-4 right-4">
            <FavoriteHeart
              type="hack"
              id={hack.id}
              size="lg"
              testId={`hack-detail-favorite-${hack.id}`}
            />
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 text-white">
            <div className="flex flex-wrap gap-2 mb-3">
              {(hack.skin_types || []).map((s) => (
                <Badge key={s} variant="secondary" className="bg-white/85 text-ink border-0 text-[10px] uppercase tracking-[0.14em]">
                  {s}
                </Badge>
              ))}
            </div>
            <h1 className="font-display text-3xl sm:text-5xl font-semibold leading-tight">{hack.name}</h1>
            <p className="mt-2 max-w-2xl text-white/90 text-sm sm:text-base">{hack.excerpt}</p>
          </div>
        </div>

        <div className="mt-8 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 space-y-8">
            {/* Ingredients */}
            <Card className="rounded-2xl bg-card border-border/70 p-6" data-testid="hack-ingredients-section">
              <h2 className="font-display text-2xl font-semibold mb-4 flex items-center gap-2 text-ink">
                <Leaf className="h-5 w-5 text-rose-gold" /> Ingredients
              </h2>
              <ul className="space-y-2.5">
                {hack.ingredients.map((it, idx) => (
                  <li key={idx} className="flex items-baseline justify-between gap-3 border-b border-border/60 last:border-0 pb-2.5 last:pb-0">
                    <span className="text-foreground">{it.name}</span>
                    {it.quantity && (
                      <span className="text-sm text-muted-foreground tracking-wide">{it.quantity}</span>
                    )}
                  </li>
                ))}
              </ul>
            </Card>

            {/* Steps */}
            <Card className="rounded-2xl bg-card border-border/70 p-6" data-testid="hack-steps-section">
              <h2 className="font-display text-2xl font-semibold mb-4 text-ink">Steps</h2>
              <ol className="space-y-4">
                {hack.steps.map((step, idx) => (
                  <li key={idx} className="flex gap-4">
                    <span className="shrink-0 h-7 w-7 rounded-full bg-accent text-rose-gold flex items-center justify-center font-sans text-sm font-semibold border border-rose-tint">
                      {idx + 1}
                    </span>
                    <p className="text-sm sm:text-base leading-relaxed text-foreground/90 pt-0.5">{step}</p>
                  </li>
                ))}
              </ol>
            </Card>

            {/* Benefits */}
            <Card className="rounded-2xl bg-card border-border/70 p-6" data-testid="hack-benefits-section">
              <h2 className="font-display text-2xl font-semibold mb-4 flex items-center gap-2 text-ink">
                <Sparkles className="h-5 w-5 text-rose-gold" /> Benefits
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {hack.benefits.map((b) => (
                  <div key={b} className="rounded-xl bg-cream-paper border border-border/60 px-4 py-3 text-sm">
                    {b}
                  </div>
                ))}
              </div>
            </Card>

            {hack.safety_note && (
              <Card className="rounded-2xl bg-accent/40 border border-rose-tint/70 p-5">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-4 w-4 text-rose-gold mt-0.5 shrink-0" />
                  <p className="text-sm text-foreground/90 leading-relaxed">
                    <strong className="font-semibold">A gentle note: </strong>
                    {hack.safety_note}
                  </p>
                </div>
              </Card>
            )}

            <ReviewsSection itemType="hack" itemId={hack.id} />
          </div>

          <aside className="lg:col-span-5">
            <Card className="rounded-2xl bg-card border-border/70 p-6 sticky top-20">
              <h3 className="font-display text-xl font-semibold text-ink">Quick facts</h3>
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-muted-foreground">Time</dt>
                  <dd className="font-medium flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{hack.prep_time}</dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-muted-foreground">Skin types</dt>
                  <dd className="flex flex-wrap gap-1 justify-end">
                    {hack.skin_types.map((s) => (
                      <Badge key={s} variant="secondary" className="text-[10px] uppercase tracking-[0.14em]">{s}</Badge>
                    ))}
                  </dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-muted-foreground">Concerns</dt>
                  <dd className="flex flex-wrap gap-1 justify-end">
                    {hack.concerns.map((c) => (
                      <Badge key={c} variant="outline" className="text-[10px] uppercase tracking-[0.14em] border-border">{c.replace('-', ' ')}</Badge>
                    ))}
                  </dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-muted-foreground">Key ingredients</dt>
                  <dd className="flex flex-wrap gap-1 justify-end">
                    {hack.key_ingredients.map((k) => (
                      <Badge key={k} className="bg-accent text-ink border border-rose-tint text-[10px] uppercase tracking-[0.14em]">{k.replace('-', ' ')}</Badge>
                    ))}
                  </dd>
                </div>
              </dl>
              <div className="mt-6 flex items-center gap-2">
                <FavoriteHeart
                  type="hack"
                  id={hack.id}
                  size="lg"
                  testId={`hack-favorite-side-${hack.id}`}
                />
                <Button asChild variant="outline" className="flex-1 rounded-xl">
                  <Link to="/diy">Browse more hacks</Link>
                </Button>
              </div>
            </Card>
          </aside>
        </div>
      </div>
    </div>
  );
}
